import React, { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import 'highlight.js/styles/github-dark.css';
import { parseEndpointsFromMarkdown } from './EndpointParser';
import { ApiTester } from './ApiTester';
import { ConfigValue } from '../AdminDocs/ConfigValue';
import './DocViewer.css';

interface DocViewerProps {
  content: string;
  /** Base path for markdown link navigation (e.g. /api-docs or /admin-docs). Defaults to /api-docs. */
  basePath?: string;
  /** Config keys in admin docs that render as revealable values */
  configKeys?: readonly string[];
}

export const DocViewer = ({ content, basePath = '/api-docs', configKeys }: DocViewerProps) => {
  const navigate = useNavigate();
  
  // Parse endpoints from markdown content
  const endpoints = useMemo(() => {
    return parseEndpointsFromMarkdown(content);
  }, [content]);

  useEffect(() => {
    // Handle anchor link clicks for smooth scrolling
    const handleAnchorClick = (e: Event) => {
      const target = e.target as HTMLElement;
      const href = target.getAttribute('href');
      
      if (!href) return;

      // Handle markdown file links (e.g., authentication.md, endpoints/seasons.md)
      if (href.endsWith('.md') || href.includes('.md#')) {
        e.preventDefault();
        
        // Convert markdown file path to route
        // Remove .md extension and convert to route path
        let routePath = href.replace(/\.md$/, '').replace(/\.md#/, '#');
        
        // If it's a relative path, construct the full route
        if (!routePath.startsWith('http')) {
          // Remove leading slash if present
          routePath = routePath.replace(/^\//, '');
          
          // Split by # to separate path and anchor
          const [filePath, anchor] = routePath.split('#');
          
          // Convert file path to route segments
          const pathParts = filePath.split('/');
          
          if (pathParts.length === 1) {
            // Single file (e.g., authentication.md -> /api-docs/authentication)
            navigate(`${basePath}/${pathParts[0]}${anchor ? `#${anchor}` : ''}`);
          } else if (pathParts.length === 2) {
            // Nested file (e.g., endpoints/seasons.md -> /api-docs/endpoints/seasons)
            navigate(`${basePath}/${pathParts[0]}/${pathParts[1]}${anchor ? `#${anchor}` : ''}`);
          } else {
            // Fallback: just navigate to the path
            navigate(`${basePath}/${filePath}${anchor ? `#${anchor}` : ''}`);
          }
        }
        return;
      }

      // Handle same-page anchor links (e.g., #getting-started)
      if (href.startsWith('#')) {
        e.preventDefault();
        const id = href.substring(1); // Remove the #
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          // Update URL without triggering navigation
          window.history.pushState(null, '', href);
        }
      }
    };

    // Handle initial hash in URL
    const handleInitialHash = () => {
      if (window.location.hash) {
        const id = window.location.hash.substring(1);
        const element = document.getElementById(id);
        if (element) {
          setTimeout(() => {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }, 100);
        }
      }
    };

    const docViewer = document.querySelector('.doc-viewer');
    if (docViewer) {
      docViewer.addEventListener('click', handleAnchorClick);
      handleInitialHash();
    }

    return () => {
      if (docViewer) {
        docViewer.removeEventListener('click', handleAnchorClick);
      }
    };
  }, [content, navigate, basePath]);

  // Generate ID from heading text (handles ReactNode)
  const generateId = (children: React.ReactNode): string => {
    // Extract text content from ReactNode
    const extractText = (node: React.ReactNode): string => {
      if (typeof node === 'string') return node;
      if (typeof node === 'number') return String(node);
      if (Array.isArray(node)) {
        return node.map(extractText).join('');
      }
      if (React.isValidElement(node)) {
        const props = node.props as { children?: React.ReactNode };
        if (props?.children) {
          return extractText(props.children);
        }
      }
      return '';
    };

    const text = extractText(children);
    return text
      .toLowerCase()
      .replace(/[^\w\s-]/g, '') // Remove special characters
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
      .trim();
  };

  // Create a map of endpoint titles to endpoint info for quick lookup
  const endpointMap = useMemo(() => {
    const map = new Map<string, typeof endpoints[0]>();
    endpoints.forEach((endpoint) => {
      // Normalize title for matching (lowercase, trim, remove extra spaces)
      const normalizedTitle = endpoint.title.toLowerCase().trim().replace(/\s+/g, ' ');
      map.set(normalizedTitle, endpoint);
    });
    return map;
  }, [endpoints]);

  return (
    <div className="doc-viewer">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeHighlight]}
        components={{
          // Customize code block rendering
          code({ className, children, ...props }) {
            const match = /language-(\w+)/.exec(className || '');
            const isInline = !match;
            if (isInline) {
              const codeContent = String(children).trim();
              if (configKeys?.includes(codeContent)) {
                return (
                  <ConfigValue
                    key={codeContent}
                    configKey={codeContent}
                  />
                );
              }
              return (
                <code className={className} {...(props as React.HTMLAttributes<HTMLElement>)}>
                  {children}
                </code>
              );
            }
            return (
              <pre className={className} {...(props as React.HTMLAttributes<HTMLPreElement>)}>
                <code className={className}>
                  {children}
                </code>
              </pre>
            );
          },
          // Customize table rendering for better styling
          table({ children }) {
            return (
              <div className="table-wrapper">
                <table>{children}</table>
              </div>
            );
          },
          // Customize heading rendering with anchor links and inject API tester
          h2({ children, ...props }) {
            const id = generateId(children);
            const titleText = typeof children === 'string' 
              ? children 
              : React.Children.toArray(children).join('');
            // Normalize title for matching (lowercase, trim, remove extra spaces)
            const normalizedTitle = titleText.toLowerCase().trim().replace(/\s+/g, ' ');
            const endpoint = endpointMap.get(normalizedTitle);
            
            return (
              <>
                <h2 id={id} {...props}>{children}</h2>
                {endpoint && <ApiTester endpoint={endpoint} />}
              </>
            );
          },
          h1({ children }) {
            const id = generateId(children);
            return <h1 id={id}>{children}</h1>;
          },
          h3({ children }) {
            const id = generateId(children);
            return <h3 id={id}>{children}</h3>;
          },
          h4({ children }) {
            const id = generateId(children);
            return <h4 id={id}>{children}</h4>;
          },
          // Customize anchor link rendering to handle markdown file links
          a({ href, children, ...props }) {
            // If it's a markdown file link, we'll handle it in the click handler
            // Just render a normal anchor tag
            return (
              <a href={href} {...(props as React.AnchorHTMLAttributes<HTMLAnchorElement>)}>
                {children}
              </a>
            );
          },
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
};

